import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutAction } from '../../slices/authSlice';
import { hasAdminPrivileges } from '../../utils/permissionsClient';
import Icon from './icons';

const initials = (user) => {
    const f = (user.firstName || '').trim()[0];
    const l = (user.lastName || '').trim()[0];
    return ((f || '') + (l || '')).toUpperCase() || (user.email || '?')[0].toUpperCase();
};

// Resolve the avatar URL we should hand to <img>. The server stores either an
// uploaded file path (/uploads/avatars/<uid>/<file>) or an external URL (e.g.
// the user's Google profile picture). The uploaded form is served from
// /api/users/avatars/:uid/:file, so we rewrite it; absolute URLs are used
// as-is.
const resolveAvatarSrc = (user) => {
    const raw = user && user.avatarUrl;
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const uid = user._id || user.id;
    if (!uid) return raw;
    const file = String(raw).split('/').pop();
    return `/api/users/avatars/${uid}/${file}`;
};

// Avatar that renders the user's photo when available and gracefully falls
// back to initials — both if no avatarUrl is set AND if the image 404s.
// className lets callers reuse their own size/shape rules (av, av-me,
// av-top, etc.); the fallback span inherits the same class.
const Avatar = ({ user, className = 'av' }) => {
    const src = resolveAvatarSrc(user);
    const [failed, setFailed] = React.useState(false);
    if (!src || failed) {
        return <div className={className}>{initials(user)}</div>;
    }
    return (
        <img
            src={src}
            alt={initials(user)}
            className={className}
            onError={() => setFailed(true)}
        />
    );
};

const ProfileMenu = ({ placement = 'up', trigger }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((s) => s.auth.user) || {};
    const isAdmin = hasAdminPrivileges(user);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const go = (path) => () => {
        setOpen(false);
        navigate(path);
    };

    const signOut = () => {
        dispatch(logoutAction());
        navigate('/login');
    };

    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'You';
    const workspace = user.organization || user.company || 'Workspace';

    return (
        <div className={`rs-pm ${placement === 'up' ? 'pm-foot' : 'pm-top'}`} ref={ref}>
            <div className="rs-pm-trigger" onClick={() => setOpen((o) => !o)}>
                {trigger(open)}
            </div>
            {open && (
                <div className={`rs-pm-pop ${placement}`} role="menu">
                    <div className="rs-pm-head">
                        <Avatar user={user} className="av" />
                        <div className="meta">
                            <b>{name}</b>
                            <span>{user.email}</span>
                        </div>
                    </div>
                    <div className={`rs-pm-role ${isAdmin ? 'admin' : ''}`}>
                        <span className="ic">{isAdmin ? Icon.shield : Icon.user}</span>
                        <span className="r">{isAdmin ? 'Workspace admin' : 'Member'}</span>
                        <span className="ws">{workspace}</span>
                    </div>
                    <div className="rs-pm-sep" />
                    <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/account')}>
                        <span className="ic">{Icon.user}</span>Your profile
                    </button>
                    <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/settings')}>
                        <span className="ic">{Icon.bell}</span>Notifications
                    </button>
                    <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/settings')}>
                        <span className="ic">{Icon.card}</span>Billing &amp; plan
                    </button>
                    <div className="rs-pm-sep" />
                    <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/settings')}>
                        <span className="ic">{Icon.gear}</span>Workspace settings
                    </button>
                    {isAdmin && (
                        <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/admin')}>
                            <span className="ic">{Icon.shield}</span>Admin console
                        </button>
                    )}
                    <button type="button" className="rs-pm-item" role="menuitem" onClick={go('/dashboard/help')}>
                        <span className="ic">{Icon.help}</span>Help &amp; support
                    </button>
                    <div className="rs-pm-sep" />
                    <button type="button" className="rs-pm-item danger" role="menuitem" onClick={signOut}>
                        <span className="ic">{Icon.signout}</span>Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

export { initials, Avatar, resolveAvatarSrc };
export default ProfileMenu;
